<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { configStore } from "@common/configStore";

const locals = {
    formatWorkflow(workflowConfig, relatedRecords, userStore, editorVisible, _this) {
        // Variables and exit conditions
        if (typeof workflowConfig !== 'object') return [];
        if (workflowConfig.ACTIVITY_ENTITY) workflowConfig.activities = workflowConfig.ACTIVITY_ENTITY;
        let activities = typeof workflowConfig.activities === 'object'
            && Object.values(workflowConfig.activities);
        if (!Array.isArray(activities)) return [];
        let getStepRecords = (step) => {
            // Returns the correct records for each activity (step)
            let recordMatcher = (type) => {
                if (!relatedRecords || typeof relatedRecords[type] !== 'object') return false;
                let records = library.cloneObj(relatedRecords[type]);
                if (!Array.isArray(records)) records = Object.values(records);
                return records.find((record) => record && record.ACTIVITY_ENTITY_ID === step.ACTIVITY_ENTITY_ID);
            };
            return {
                aff: recordMatcher('affiliations'),
                lbe: recordMatcher('lbes'),
                waiver: recordMatcher('waivers'),
                training: recordMatcher('trainings'),
                shift: recordMatcher('shifts'),
            };
        };
        // Mapping/Formatting Functions
        let setLogicRecords = (serverLogic, step) => {
            let output = [];
            let records = step.relatedRecords;
            if (!library.isJson(serverLogic)) return output;
            let logicObj = JSON.parse(serverLogic);
            let logicRule = logicObj['[FilterEditor::FiltersColumns]'];
            if (Array.isArray(logicRule)) {
                for (let i in logicRule) {
                    if (i === 'length') continue;
                    if (typeof logicRule[i].CLAUSE_TITLE !== 'string') continue;
                    let logicField = logicRule[i].CLAUSE_TITLE.toLowerCase();
                    output[i] = { criteriaRecord: 'dynamically' };
                    // Set the perspective
                    let logicPsp = logicRule[i].FLT_SELECT_FIELD_PERSPECTIVE_ABBREV;
                    if (logicPsp === 'lbe') {
                        if (logicRule[i].LBE_TYPE === 5) logicPsp = 'waiver'; // NOSONAR
                        if (logicRule[i].LBE_TYPE === 6) logicPsp = 'training'; // NOSONAR
                    }
                    let assignmentSubRecord = logicRule[i].FLT_CLAUSE_ADDITIONAL_PARAM; // Sub-record is related orgs (eg: tier 500) ie: orgKind
                    // Check for is assignment record
                    if (logicField === 'aff:assignment status') {
                        output[i].recordId = relatedRecords.ASSIGNMENT_ID;
                    }
                    // Check for a selected "Sub Record" from the assignment
                    else if (assignmentSubRecord && assignmentSubRecord !== 2) {
                        if (assignmentSubRecord === 1) output[i].recordId = relatedRecords.ASSIGNMENT_ID;
                        else if (assignmentSubRecord === 9999) output[i].recordId = relatedRecords.DETAIL_RECORD_ID; // NOSONAR
                        else if (logicField.indexOf('aff') === 0) output[i].recordId = relatedRecords?.ASSIGNMENT?.LINKED_AFFILIATIONS?.[assignmentSubRecord];
                        else output[i].recordId = relatedRecords?.ASSIGNMENT?.LINKED_RECORDS?.[assignmentSubRecord];
                    }
                    // Otherwise link using the perspective
                    else if (logicPsp === 'aff') output[i].recordId = records.aff && records.aff.AFFILIATION_ID;
                    else if (logicPsp === 'lbe') output[i].recordId = records.lbe && records.lbe.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'waiver') output[i].recordId = records.waiver && records.waiver.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'training') output[i].recordId = records.training && records.training.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'sch') {
                        // output[i].recordId = records.shift && records.shift.SCHEDULE_SLOT_ID;
                        output[i].criteriaRecord = 'object2';
                    }
                    else if (logicPsp === 'client') {
                        output[i].criteriaRecord = 'component.ecUserProperty';
                    }
                }
            }
            return output;
        };
        let mapTemplates = (tItem, step, hideWhenComplete, hideWhenDisabled) => {
            tItem.template = tItem.TEXT;
            tItem.include = tItem.IS_DEFAULT !== 0 || !tItem.LOGIC;
            if (tItem.LOGIC) {
                let logicRecords = setLogicRecords(tItem.LOGIC, step);
                let criteria = library.parseLogicToFront(tItem.LOGIC, logicRecords);
                tItem.includeWhen = [{ criteria, resultType: 'returnCriteria' }];
            }
            tItem.hideWhenDisabled = hideWhenDisabled;
            tItem.hideWhenComplete = hideWhenComplete;
            return tItem;
        };
        let parseActionLogic = (action, step, logicType) => {
            if (action[logicType]) {
                let logicRecords = setLogicRecords(action[logicType], step);
                let criteria = library.parseLogicToFront(action[logicType], logicRecords);
                return [{ criteria, resultType: 'returnCriteria' }];
            } return false;
        };
        let mapActions = (action, step, hideWhenComplete, hideWhenDisabled) => {
            let tAction = library.cloneObj(action);
            tAction.label = tAction.TEXT;
            // Convert legacy config to standard
            // TODO: Remove this code after all configurations have been updated https://samaritan.atlassian.net/browse/DEVD-1202
            if (tAction.ACTION
                && typeof tAction.ACTION === 'object'
                && !Array.isArray(tAction.ACTION)
                && tAction.ACTION.params
            ) {
                let chainedActions = [];
                if (tAction.ACTION.params.actionChaining) {
                    chainedActions = library.cloneObj(tAction.ACTION.params.actionChaining);
                    delete tAction.ACTION.params.actionChaining;
                }
                let formattedVal = [tAction.ACTION];
                formattedVal = formattedVal.concat(chainedActions);
                tAction.action = formattedVal;
            } else tAction.action = tAction.ACTION;
            tAction.actionIcon = tAction.ACTION_ICON;
            tAction.include = !!tAction.IS_DEFAULT;
            if (tAction.CSS_CLASSES) tAction.cssClasses = tAction.CSS_CLASSES;

            tAction.includeWhen = parseActionLogic(tAction, step, 'LOGIC');
            tAction.disableWhen = parseActionLogic(tAction, step, 'DISABLE_LOGIC');

            tAction.hideWhenDisabled = hideWhenDisabled;
            tAction.hideWhenComplete = hideWhenComplete;
            return tAction;
        };
        let processStepLogic = (step) => {
            let v1 = 1; // By Default
            let v2 = 2; // Use Logic Editor
            let v3 = 3; // Use Volunteer's Affiliation Status
            // Set include property
            let setStepState = (stepKey) => {
                // stepKey is one of: include, enable, complete
                let partialKey = 'AE_' + stepKey.toUpperCase() + 'D'; // eg: AE_INCLUDED, AE_ENABLED, AE_COMPLETED
                let sOption = step[partialKey + '_BY_DEFAULT']; // eg: 1, 2, 3
                let affStatusKey = partialKey + '_AFF_STATUS_ID'; // eg: AE_INCLUDED_AFF_STATUS_ID
                let logicKey = partialKey + '_LOGIC_FILTER_ID'; // eg: AE_INCLUDED_LOGIC_FILTER_ID
                let criteria;
                let logicActions;
                if (!sOption || sOption === v1) {
                    criteria = {
                        rules: [{
                            field: partialKey + '_BY_DEFAULT',
                            operator: 'equal',
                            type: 'numeric',
                            value: v1,
                            criteriaRecord: 'object1' // previous step completed
                        }],
                        condition: 'and'
                    };
                }
                else if (sOption === v2 && step[logicKey]) {
                    let logicRecords = setLogicRecords(step[logicKey], step);
                    criteria = library.parseLogicToFront(step[logicKey], logicRecords);
                }
                else if (sOption === v3) {
                    let stepRecords = step.relatedRecords;
                    // Map selected status option from activity profile to logic criteria
                    if (stepRecords.aff) {
                        criteria = {
                            rules: [{
                                label: 'AFF:Status',
                                field: 'AFF_STATUS_ID',
                                operator: 'equal',
                                type: 'numeric',
                                value: step[affStatusKey],
                                psp: 'aff',
                                criteriaRecord: 'dynamically',
                                recordId: stepRecords.aff.AFFILIATION_ID
                            }],
                            condition: 'and'
                        };
                    }
                }
                if (criteria) step[stepKey + 'When'] = [{ criteria, resultType: 'returnCriteria' }];
            };
            setStepState('include');
            setStepState('enable');
            setStepState('complete');
            let setOnChangeActions = () => {
                if (!step['AE_ENABLED_SET'] || workflowConfig?.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES) return; // Only care if we have the setting
                let stepRecords = step.relatedRecords;
                let newStatus = parseInt(step.AE_ENABLED_SET_AFF_STATUS_ID, 10) > 0 && step.AE_ENABLED_SET_AFF_STATUS_ID;
                let newReason = parseInt(step.AE_ENABLED_SET_AFF_REASON_ID, 10) > 0 && step.AE_ENABLED_SET_AFF_REASON_ID;
                let newDate = typeof step.AE_ENABLED_SET_AFF_START_DATE === 'string' && step.AE_ENABLED_SET_AFF_START_DATE ? step.AE_ENABLED_SET_AFF_START_DATE : false;
                let currStatus = stepRecords?.aff?.AFF_STATUS_ID;
                if (!newStatus || currStatus !== 1) return;
                // Map selected status option from activity profile to logic criteria
                let onChangeCriteria = {
                    rules: [{ // This rule is intentionally duplicating the logic in line above "stepRecords.aff.AFF_STATUS_ID..."
                        label: 'AFF:Status ID',
                        field: 'AFF_STATUS_ID',
                        operator: 'equal',
                        type: 'numeric',
                        value: 1,
                        psp: 'aff',
                        criteriaRecord: 'dynamically',
                        recordId: stepRecords.aff.AFFILIATION_ID
                    }, {
                        field: 'complete',
                        operator: 'equal',
                        type: 'boolean',
                        value: true,
                        criteriaRecord: 'object3' // previous step completed
                    }],
                    condition: 'and'
                };
                // Put the complete onChangeActions configuration into the step configuration
                let onChangeActions = [
                    {
                        logic: [{
                            criteria: onChangeCriteria,
                            resultType: 'returnCriteria',
                        }],
                        actions: [
                            {
                                action: 'updateAffiliation',
                                params: {
                                    affID: stepRecords.aff.AFFILIATION_ID,
                                    AFF_STATUS: newStatus,
                                    AFF_REASON: newReason, // It might be false
                                    AFF_START_DATE: newDate,
                                    disableResponseMessage: true
                                }
                            }
                        ],
                        executeIfLogicResult: true,
                        runOnce: true
                    }
                ];
                step.onChangeActions = onChangeActions;
            };
            setOnChangeActions();
        };
        let processExpiration = (step) => {
            // return if status ID isn't 3 (expiring)
            let complianceStatus = step?.relatedRecords?.aff?.AFF_COMPLIANCE_STATUS_ID;
            let affStatus = step?.relatedRecords?.aff?.AFF_STATUS_ID;
            if (complianceStatus === 3 || (complianceStatus === 5 && affStatus !== 4)) step.cssClasses += ' item-expiring';
            if (complianceStatus !== 3) return;
            const EXPIRATION_DATE = step?.relatedRecords?.aff?.AFF_EXPIRATION_DATE || false;
            const EXPIRATION_MESSAGE = 'This task is about to expire, click the button to begin renewal.';
            const expirationTemplate = {
                template: `<div class="activity-expiration expiration-flag">
                                <div class="expiration-text">
                                    <strong style="display: flex">
                                        <i class="fa-regular fa-triangle-exclamation"></i>
                                        Expiring Soon
                                        <span v-if="${EXPIRATION_DATE}"
                                              style="font-weight: normal; color: #4c4c4c; margin-left: 6px"
                                        >- ${EXPIRATION_DATE}</span>
                                    </strong>
                                    <span>
                                        ${EXPIRATION_MESSAGE}
                                    </span>
                                </div>
                                <button type="button"
                                        class="coordinator-action-button"
                                        title="Renew"
                                        @click="actionStore.renewActivity(
                                                    {
                                                        currentAffId: record.AFFILIATION_ID,
                                                        volId: record.VOLUNTEER_ID,
                                                        activityId: record.ACTIVITY_ENTITY_ID,
                                                        repeatOrgId: '',
                                                        fields: [
                                                            { field: 'AFF_STATUS', value: 3},
                                                            { field: 'AFF_REASON', value: 503},
                                                            { field: 'AFF_COMPLIANCE_STATUS', value: 5},
                                                        ]
                                                    })"
                                ><span>Renew</span>
                                </button>
                           </div>`,
                cssClasses: 'renew-message'
            };

            step.descriptions.unshift(expirationTemplate);
        };
        // EXECUTE: Process all the activities
        activities = library.cloneObj(activities);
        return activities.map((step) => {
            if (typeof step !== 'object') return {};
            step.relatedRecords = getStepRecords(step);
            step.aeId = step.ACTIVITY_ENTITY_ID;
            step.volId = _this.contextVolId || step.VOLUNTEER_ID;
            if (!library.objIsEmpty(step.relatedRecords)) {
                for (let psp in step.relatedRecords) {
                    if (typeof step.relatedRecords[psp]?.ID !== 'undefined') step[psp + 'Id'] = step.relatedRecords[psp].ID;
                }
            }
            if (typeof step.AE_CSSCLASSES !== 'undefined') step.cssClasses = step.AE_CSSCLASSES;
            if (step.AE_IS_COMPLIANCE) {
                const complianceClass = 'compliance-status-' + step?.relatedRecords?.aff?.AFF_COMPLIANCE_STATUS_ID;
                step.cssClasses = step.cssClasses === 'undefined' ? complianceClass : step.cssClasses + ' ' + complianceClass;
            }
            // Hide for coordinator users that cannot see the activity
            let isVisible = !userStore?.coordinator?.availableActivities
                || !!userStore.coordinator.availableActivities.find((ca) => step.ACTIVITY_ENTITY_ID === ca.ACTIVITY_ENTITY_ID);
            step.isVisible = isVisible;
            if (step.isVisible) {
                if (Array.isArray(step.ACTIVITY_ENTITY_TITLE)) {
                    if (!step.ACTIVITY_ENTITY_TITLE.length) step.titles = [{ TEXT: step.AE_TITLE, template: `<div>${step.AE_TITLE}</div>` }];
                    else step.titles = step.ACTIVITY_ENTITY_TITLE.map((title) => mapTemplates(title, step));
                }
                if (Array.isArray(step.ACTIVITY_ENTITY_MESSAGE)) step.descriptions = step.ACTIVITY_ENTITY_MESSAGE.map((description) => mapTemplates(description, step, !description.SHOW_WHEN_COMPLETE, true));
                if (Array.isArray(step.ACTIVITY_ENTITY_ACTION)) step.actions = step.ACTIVITY_ENTITY_ACTION.map((action) => mapActions(action, step, !action.SHOW_WHEN_COMPLETE, true));
            }
            else {
                step.titles = [{
                    template: "<div>Step Unavailable</div>",
                    include: true
                }];
                step.descriptions = [{
                    template: "<div>This step is not visible to you.</div>",
                    include: true
                }];
                step.actions = [];
                step.cssClasses += ' step-not-visible';
                step.excludeFromCount = true;
            }
            processStepLogic(step);
            // Set up the sub-record merging
            step.enableSubRecordMerging = true;
            step.subRecordMergeRules = [
                {
                    subSourceKey: 'affiliations',
                    subRecordFilter: 'this.subRecord.ACTIVITY_ENTITY_ID === this.item.ACTIVITY_ENTITY_ID',
                    mergeWithParent: true,
                    // If changing this, also change the history template that is added to ste descriptions
                },
                {
                    subSourceKey: 'waivers',
                    subRecordFilter: 'this.subRecord.ACTIVITY_ENTITY_ID === this.item.ACTIVITY_ENTITY_ID',
                    parentOutputKey: 'stepWaiver',
                },
                {
                    subSourceKey: 'shifts',
                    subRecordFilter: 'this.subRecord.AE_AFFILIATION_ID === this.record.AFFILIATION_ID',
                    mergeWithParent: true,
                },
                {
                    subSourceKey: 'lbes',
                    subRecordFilter: 'this.subRecord.ACTIVITY_ENTITY_ID === this.item.ACTIVITY_ENTITY_ID',
                    parentOutputKey: 'stepLbe',
                },
                {
                    subSourceKey: 'trainings',
                    subRecordFilter: 'this.subRecord.ACTIVITY_ENTITY_ID === this.item.ACTIVITY_ENTITY_ID',
                    parentOutputKey: 'stepTraining',
                }
            ];
            if (!editorVisible) {
                let adminControls = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button" title="Edit Affiliation"
                            @click="actionStore.editAffiliation({ affID: record.AFFILIATION_ID, showInDialog: { AFF_STATUS: true, AFF_REASON: true, AFF_NOTE: true, AFF_START_DATE_TYPE: true, AFF_START_DATE: true, AFF_EXPIRATION_DATE_TYPE: true, AFF_EXPIRATION_DATE: true } })"
                        ><span class="h-button-text">Edit</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_IS_ADMIN',
                                type: 'string',
                                operator: 'equal',
                                value: 'yes',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let coordinatorControls = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button" title="View History"
                            @click="actionStore.openComponentDialog(
                                {
                                    componentId: 'itemList_affiliation_history__workflow_step_' + index,
                                    contextRecordIds: { affId: record.AFFILIATION_ID },
                                    dialogSettings: {title: 'Activity History'},
                                    component: {
                                        volId: component.volId,
                                        oppId: component.oppId,
                                        orgId: component.orgId,
                                        lbeId: component.lbeId,
                                        schId: component.schId,
                                        affId: component.affId,
                                        aeId: component.aeId
                                    }
                                }
                            )"
                        ><span class="h-button-text">View History</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let coordinatorMessages = {
                    template: `<div>
                        <div class="aff-info-text">{{record.affStatusText}}</div>
                        <div class="aff-info-text">{{record.affReasonText}}</div>
                        <div class="aff-info-text">(ID: {{record.AFFILIATION_ID}})</div>
                    </div>`,
                    cssClasses: 'coordinator-info',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let refreshStep = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button" title="Refresh Step"
                            @click="actionStore.refreshWorkflowStep({ record })"
                        ><span class="h-button-text">Refresh</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let expirationMessage = {
                    template: `<div v-if="record.AFF_EXPIRATION_DATE">
                        <span class="expiration-flag"
                            v-if="moment().isSameOrAfter(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY').subtract(30, 'days'))
                            && moment().isBefore(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY'))">
                            Status: <span class="expiring">Expiring soon {{record.AFF_EXPIRATION_DATE}}
                            <span v-if="record.AFF_COMPLIANCE_STATUS_ID === 5"> (Renewal Started)</span>
                            </span>
                        </span>
                        <span class="expiration-flag"
                              v-else-if="moment().isSameOrAfter(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY'))"
                              >Status: <span class="expired">Expired {{record.AFF_EXPIRATION_DATE}}</span></span>
                        <span class="expiration-flag" v-else>
                            <span class="current">Current expires: {{record.AFF_EXPIRATION_DATE}}</span></span>
                    </div>`,
                    cssClasses: 'expiration-status'
                };
                processExpiration(step);
                step.descriptions.push(...[expirationMessage, coordinatorMessages, coordinatorControls, adminControls, refreshStep]);
            }
            // TODO: Add other merge rules
            return step;
        });
    }
};

const editorSettings = {
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'restrictWorkflowTypes'
        ]
    },
    restrictWorkflowTypes: {
        type: 'CHECKBOX_LIST',
        label: 'Restrict Workflow Types',
        id: 'restrictWorkflowTypes',
        default: {},
        itemsSource: 'configStore.workflowOptions()',
        helpText: 'Pick a workflow type to filter. To show all, leave blank.',
        placeholder: 'Pick a workflow type to filter. To show all, leave blank.'
    },
};

const workflowViewerConfig = {
    cId: "workflowViewer",
    cType: "workflowViewer",
    cTitle: "Workflow Viewer",
    cTemplate: `<div v-if="logicParserReady">
            <div v-if="editorWorkflowConfig">
                <!-- Display editor workflow -->
                <item-list
                    @update:targetedItem="targetItem"
                    @update:itemOrder="updateOrder"
                    :prop-items-list="editorWorkflowConfig"
                    :prop-relative-records="editorRecord"
                    :prop-editor-visible="editorVisible"
                    :context-vol-id="volId"
                    c-id="itemList_workflow_editor__0"
                    c-type="itemList"
                ></item-list>
            </div>
            <div v-else-if="assignmentList && !assignmentsLoaded" class="workflow-loader">
                <div class="loader-animation-container">
                    <div class="loader-animation">
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                    </div>
                </div>
                <p>Loading assignments... please wait.</p>
            </div>
            <div v-else-if="assignmentsLoaded && !workflowExists" class="no-workflow">
                <p class="no-workflows-message">There are no workflows available.</p>
            </div>
            <div v-else-if="assignmentsLoaded && workflowExists && !visibleWorkflows" class="no-workflow">
                <p class="no-workflows-message">There are no visible workflows, some workflows are hidden.</p>
                <control name="SHOW_HIDDEN_WORKFLOWS"></control>
            </div>
            <div v-else-if="workflowTabs.length">
                <div v-if="waitingFirstWorkflowLoad" class="workflow-loader">
                    <div class="loader-animation-container">
                        <div class="loader-animation">
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                        </div>
                    </div>
                    <p>Loading workflow tabs and details... Please wait.</p>
                </div>
                <div v-show="!waitingFirstWorkflowLoad">
                    <control name="SHOW_HIDDEN_WORKFLOWS"></control>
                    <tab-list cId="tabList_workflow" :prop-tabs-list="workflowTabs" :prop-selected-tab="selectedTab" @update:selectedTab="changeSelectedTab">
                        <template v-for="(tab, index) in workflowTabs" #[tab.slotTemplateId]>
                            <div class="workflow-header">
                                <div class="workflow-loader">
                                    <div class="loader-animation-container" v-if="loaderVisible">
                                        <div class="loader-animation">
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                        </div>
                                    </div>
                                    <p v-show="!formattedWorkflowConfigs[tab.wfTabId] || !formattedWorkflowConfigs[tab.wfTabId].length">
                                        Loading workflow details... Please wait.
                                    </p>
                                    <p v-show="childPendingSubscriptions.length">
                                        Getting step details... Almost there.
                                    </p>
                                </div>
                                <div class="workflow-status">
                                    <span class="assignment-id">(Assignment: {{tab.assignmentId}})</span>
                                    Workflow Status: {{tab.workflowStatus}}
                                    <button v-if="isAdminRole" type="button" class="coordinator-action-button" title="Update Status"
                                        @click="actionStore.updateWorkflowStatus({
                                        assignmentId: tab.assignmentId,
                                        workflowId: tab.workflowId})"><span class="h-button-text">Update Status</span>
                                    </button>
                                </div>
                            </div>
                            <div v-if="formattedWorkflowConfigs[tab.wfTabId]">
                                <div v-show="selectedTab === tab.tabIndex || tab.loading">
                                    <item-list
                                        @update:targetedItem="targetItem"
                                        @update:itemOrder="updateOrder"
                                        @update:completedItems="updateCompletedItems(tab.wfTabId, $event)"
                                        @update:includedItems="updateIncludedItems(tab.wfTabId, $event)"
                                        @update:enabledItems="updateEnabledItems(tab.wfTabId, $event)"
                                        :prop-items-list="formattedWorkflowConfigs[tab.wfTabId]"
                                        :prop-relative-records="workflowRecords[tab.wfTabId]"
                                        :prop-editor-visible="editorVisible"
                                        :context-vol-id="volId"
                                        :c-id="'itemList_workflow__' + index"
                                        c-type="itemList"
                                        :ref="cId + '_itemList'"
                                        @update:pendingSubscriptions="childPendingSubscriptions = $event"
                                    ></item-list>
                                </div>
                            </div>
                        </template>
                    </tab-list>
                </div>
            </div>
    </div>`,
    controls: [
        {
            name: 'SHOW_HIDDEN_WORKFLOWS',
            label: 'Show Hidden Workflows',
            type: 'boolean',
            inlineLabelReverse: true,
            logic: [{
                criteria: '!!this.component.ecUserProperty.CLIENT_ROLE_OPTION',
                prop: 'display',
                valueRecord: 'self',
                valueType: 'criteria'
            }],
            onchange: 'loadWorkflowTabs'
        }
    ]
};

const workflowViewerCmp = {
    name: "workflowViewer",
    editorSettings: editorSettings,
    props: {
        editorWorkflowSettings: {
            type: Object
        },
        workflowId: {
            default: false
        },
        editorVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            targetedItem: false,
            workflowTabsSettings: {},
            workflowTabs: [],
            waitingFirstWorkflowLoad: false,
            serverAffiliations: {},
            statusOptions: [],
            reasonOptions: [],
            editorRecord: [{}], // Placeholder record
            relatedRecords: {
                // Configured from the sds_getWorkflowDetails response
                // Example:
                // 10: {
                //     affiliations: {},
                //     lbes: {},
                //     waivers: {},
                //     trainings: {},
                //     shifts: {},
                // }
            },
            shiftFields: [
                'SS_TITLE',
                'SS_DESC',
                'SS_NOTE',
                'SS_DATE',
                'SS_START',
                'SS_FINISH_TIME',
                'SS_END_TIME',
                'SS_START_TIME',
                'SS_PICKUP_INSTR',
                'SA_SSLI_ADDRESS_1',
                'SA_SSLI_ADDRESS_2',
                'SA_SSLI_CITY',
                'SA_SSLI_COUNTRY',
                'SA_SSLI_POSTAL_CODE',
                'SA_SSLI_STATE',
                'SA_SSLI_PHONE',
                'SA_SSLI_CONTACT_PERSON',
                'SA_SSLI_CONTACT_PHONE',
                'ACTIVITY_ENTITY_ID',
                'ASSIGNMENT_ID',
                'AE_AFFILIATION_ID'
            ],
            affFields: [
                'AFFILIATION_ID',
                'DETAIL_RECORD_ID',
                'AFF_REASON_ID',
                'AFF_REASON',
                'AFF_STATUS_ID',
                'AFF_STATUS',
                'AFF_START_DATE',
                'AFF_END_DATE',
                'AFF_COMPLIANCE_STATUS',
                'AFF_COMPLIANCE_STATUS_ID',
                'AFF_COMPLIANCE_STATUS_VERIFIER',
                'AFF_STATUS_VERIFIER_DATE',
                'ACTIVITY_ENTITY_TITLE',
                'ACTIVITY_ENTITY_ID',
                'AFF_ASSIGNMENT_WORKFLOW_STATUS',
                'AFF_STATUS_VERIFIER',
                'AFF_STATUS_DATETIME',
                'AFF_COMPLETE_DATE',
                'AFF_EXPIRATION_DATE',
                'AFF_VOLUNTEER',
            ],
            user: false,
            logicParserReady: false,
            selectedTab: null,
            workflowStepStatus: {},
            isWorkflowFullyLoad: false,
            isWorkflowFullyLoadTimeout: false,
            assignmentsLoaded: false,
            workflowExists: true,
            childPendingSubscriptions: []
        };
    },
    watch: {
        contextOrgId(orgId) {
            const orgTab = this.workflowTabs.find((tab) => tab.orgId === orgId) || this.workflowTabs[0];
            // this.workflowTabs.forEach((tab) => { tab.show = tab.orgId === orgId || !orgId; });
            this.updateTabVisibility();
            this.changeSelectedTab(orgTab?.tabIndex);
        }
    },
    computed: {
        editorWorkflowConfig() {
            if (this.editorWorkflowSettings) return locals.formatWorkflow(this.editorWorkflowSettings, false, this.userStore, this.editorVisible, this);
            return false;
        },
        isAdminRole() {
            const coordinator = this.userStore.coordinator;
            return coordinator?.CLIENT_IS_ADMIN === 'Yes' || coordinator?.CLIENT_IS_STAFF === 'Yes';
        },
        formattedWorkflowConfigs() {
            // Take responses from sds_getWorkflowDetails from inside workflowTabs and format the workflow
            // This computed property powers the itemLists in the template
            let output = {};
            if (this.editorWorkflowConfig || library.objIsEmpty(this.workflowTabsSettings)) return output;
            // Iterate through all workflows and format the config
            for (let tabId in this.workflowTabsSettings) {
                if (tabId === 'length') continue;
                let workflowConfig = this.workflowTabsSettings[tabId];
                let relatedRecords = this.workflowRecords[tabId] ? (this.workflowRecords[tabId]?.[0] || this.workflowRecords[tabId]) : {};
                output[tabId] = locals.formatWorkflow(workflowConfig, relatedRecords, this.userStore, false, this);
                // Process include, enable and complete logic here so the itemList doesn't need to be loaded
                // TODO: Setup processing steps without having to load the itemList
            }
            return output;
        },
        workflowRecords() {
            let records = {};
            // Get vol's activity shifts
            let startDate = library.getMoment().subtract(1, 'day').format('MM/DD/YYYY');
            let endDate = library.getMoment().add(60, 'days').format('MM/DD/YYYY');
            this.recordStore.subscribe({
                source: ['er_getPlacedSlotList'],
                updateFrequency: 60,
                params: {
                    volId: this.contextVolId,
                    fields: this.shiftFields,
                    activityEntitySlots: true,
                    startDate,
                    endDate
                }
            });
            let volSub = this.recordStore.subscribe({
                source: ['er_volFields'],
                updateFrequency: 15,
                params: { recordId: this.contextVolId, fields: ['VOL_FIRST_NAME', 'VOL_LAST_NAME', 'VOL_AGE', 'VOLUNTEER_ID'] }
            });
            let volRecord = this.recordStore.getSubscription(volSub);
            if (volRecord) volRecord = volRecord[this.contextVolId];
            this.workflowTabs.forEach((tab) => {
                let wfId = tab.workflowId;
                let wfTabId = tab.wfTabId;
                if (!this.relatedRecords[wfTabId]) this.relatedRecords[wfTabId] = {};
                let relatedRecords = this.relatedRecords[wfTabId];
                relatedRecords.affiliations = this.activityAffiliations[wfTabId];
                let record = {
                    WORKFLOW_ID: wfId,
                    shifts: volRecord?.shifts, // Use vol's shifts from the recordStore
                    assignmentId: relatedRecords?.ASSIGNMENT?.ID,
                    ASSIGNMENT_ID: relatedRecords?.ASSIGNMENT?.ID,
                    ...volRecord,
                    ...relatedRecords
                };
                records[wfTabId] = [record];
            });
            return records;
        },
        assignmentList() {
            if (this.contextVolId <= 0) return {};
            let assignmentsSub = this.recordStore.subscribe({
                source: ['sds_getAssignmentList'],
                updateFrequency: 360,
                params: {
                    volId: this.contextVolId,
                    fields: { 3: ['ORG_TITLE', 'ORGANIZATION_ID', 'WORKFLOW_ID', 'WORKFLOW_TITLE'], 32: ['AFF_ASSIGNMENT_WORKFLOW_STATUS', 'AFF_STATUS_ID'] },
                    params: { strictTypeResults: true }
                }
            }, this.cId);
            if (assignmentsSub.lastRequestTime) {
                this.assignmentsLoaded = true; // Only sets to true on first loaded, not on polling updates
            }
            let records = library.cloneObj(this.recordStore.getSubscription(assignmentsSub.key));
            if (!library.objIsEmpty(records)) {
                setTimeout(() => {
                    this.loadWorkflowTabs();
                }, 10);
            }
            return records;
        },
        activityAffiliations() {
            let output = {};
            // Affiliation Formatter
            let formatActivityAffiliations = (affiliations) => {
                if (typeof affiliations !== 'object') return {};
                for (let affId in affiliations) {
                    if (affId === 'length') continue;
                    let affRecord = affiliations[affId]; // This will be undefined until the subscription returns data
                    if (!affRecord) continue;
                    if (!affRecord.ACTIVITY_ENTITY_ID && affRecord.AFF_RECORDS?.[8]) {
                        affRecord.ACTIVITY_ENTITY_ID = Number(affRecord.AFF_RECORDS[8][0]);
                    }
                    if (!affRecord.HISTORY) affRecord.HISTORY = {};
                    affRecord.HISTORY = Object.values(affRecord.HISTORY).reverse();
                    // eslint-disable-next-line no-loop-func
                    let statusOption = this.statusOptions.find((status) => status.value === affRecord.AFF_STATUS_ID);
                    if (typeof statusOption?.text !== 'undefined') affRecord.affStatusText = statusOption.text;
                    // eslint-disable-next-line no-loop-func
                    let reasonOption = this.reasonOptions.find((status) => status.value === affRecord.AFF_REASON_ID);
                    if (typeof reasonOption?.text !== 'undefined') affRecord.affReasonText = reasonOption.text;
                }
                // TODO setup a subscribe
                return affiliations;
            };
            // if (!this.serverAffiliations[tab.wfTabId]) return {};
            // Subscribe to the vol->activity affiliations
            let subscriptionRecords = {};
            if (!this.waitingForInitialLoad) {
                let allAffiliations = [];
                for (let tabIndex in this.workflowTabs) {
                    let wfTabId = this.workflowTabs[tabIndex].wfTabId;
                    if (!this.serverAffiliations[wfTabId]) continue;
                    let affIds = Object.keys(this.serverAffiliations[wfTabId]);
                    affIds = affIds.map((affId) => Number(affId));
                    allAffiliations = allAffiliations.concat(affIds);
                }
                allAffiliations = [...new Set(allAffiliations)];
                if (!allAffiliations.length) return output;
                let payload = {
                    source: 'sds_getProfileField',
                    params: {
                        recordPsp: 'aff',
                        recordId: allAffiliations,
                        fields: this.affFields,
                        // params: { strictTypeResults: true }
                    },
                    updateFrequency: 30,
                };
                let subscription = this.recordStore.subscribe(payload, this.cId, false, false);
                subscriptionRecords = this.recordStore.getSubscription(subscription.key);
                subscriptionRecords = library.cloneObj(subscriptionRecords);
                if (subscriptionRecords) {
                    // Iterate and substitute
                    for (let sAffId in subscriptionRecords) {
                        if (sAffId === 'length') continue;
                    }
                }
            }
            for (let tabIndex in this.workflowTabs) {
                if (tabIndex === 'length') continue;
                let wfTabId = this.workflowTabs[tabIndex].wfTabId;
                let initialRecords = this.serverAffiliations[wfTabId];
                if (!initialRecords) continue;
                let thisTabRecords = {};
                let affIds = Object.keys(initialRecords);
                // eslint-disable-next-line no-loop-func
                const findRenewalAff = (affId) => {
                    // Recurse until we have no new complianceRenewalAffId property on the record
                    let newAffId = subscriptionRecords[affId]?.complianceRenewalAffId || affId;
                    if (subscriptionRecords[newAffId]?.complianceRenewalAffId) {
                        return findRenewalAff(subscriptionRecords[newAffId].complianceRenewalAffId);
                    }
                    return newAffId;
                };
                affIds.forEach((affId) => {
                    // NOTE: We are using either subscription data or server data here
                    let currAffId = findRenewalAff(affId);
                    thisTabRecords[currAffId] = subscriptionRecords[currAffId] || initialRecords[currAffId];
                });
                output[wfTabId] = formatActivityAffiliations(thisTabRecords);
            }
            return output;
        },
        // waitingForInitialLoad() {
        //     return Object.values(this.workflowTabsSettings).find((tab) => tab.loading);
        // },
        visibleWorkflows() {
            return this.workflowTabs.find((tab) => tab.show);
        },
        loaderVisible() {
            let tab = this.workflowTabs.find((iTab) => iTab.tabIndex === this.selectedTab);
            if (!tab) return false;
            return !this.formattedWorkflowConfigs[tab.wfTabId]
                || !this.formattedWorkflowConfigs[tab.wfTabId].length
                || this.childPendingSubscriptions.length;
        }
    },
    methods: {
        targetItem(item, event) {
            this.$emit('update:targetedItem', item, event);
        },
        updateOrder(orderData) {
            this.$emit('update:itemOrder', orderData);
        },
        updateCompletedItems(wfTabId, items) {
            this.workflowStepStatus[wfTabId].complete = Array.isArray(items) ? items.length : 0;
        },
        updateIncludedItems(wfTabId, items) {
            this.workflowStepStatus[wfTabId].include = Array.isArray(items) ? items.length : 0;
            if (Array.isArray(items)) { // TODO Temp fix for workflow with no included steps, need a better fix for "loaded" criteria
                setTimeout(() => {
                    this.workflowTabsSettings[wfTabId].loading = false;
                }, 500);
            }
        },
        updateEnabledItems(wfTabId, items) {
            this.workflowStepStatus[wfTabId].enable = Array.isArray(items) ? items.length : 0;
        },
        changeSelectedTab(tabVal) {
            this.selectedTab = tabVal;
            this.loadWorkflowDetails(this.workflowTabs[tabVal]);
        },
        async onCreate() {
            await library.prepLogicParsingToFront();
            this.logicParserReady = true;
            this.statusOptions = await configStore.attrListOptions("aff_status_2");
            this.reasonOptions = await configStore.attrListOptions("aff_reason_2");
        },
        updateTabVisibility() {
            let contextOrg = this.orgId;
            this.workflowTabs.forEach((wfTab) => {
                let show = false;
                if (this.controls.SHOW_HIDDEN_WORKFLOWS.value) show = true;
                else if (wfTab.workflowStatus === 'In Progress' && (!contextOrg || wfTab.orgId === contextOrg)) show = true;
                else if (wfTab.workflowStatus === 'Waiting' && (!contextOrg || wfTab.orgId === contextOrg)) show = this.workflowStepStatus[wfTab.wfTabId].enable;
                wfTab.show = show;
            });
        },
        // eslint-disable-next-line consistent-return
        loadWorkflowTabs() {
            // Generate tabs based off of assignment orgs with workflows
            if (this.editorWorkflowConfig || library.objIsEmpty(this.assignmentList)) return this.workflowTabs;
            let tabIndex = 0;
            // Process each assignment
            // Check for workflows that aren't "Completed"
            // Find any org that has attached workflow and create a tab
            let workflowExists = false;
            Object.values(this.assignmentList).forEach((assignment) => {
                let assignmentOrgs = assignment?.LINKED_RECORDS ? Object.values(assignment.LINKED_RECORDS) : [];
                assignmentOrgs.forEach((aOrgId) => {
                    // TODO call all orgs at same time as assignments
                    let wfRecord = this.recordStore.records.org[aOrgId];
                    let workflowId = wfRecord && wfRecord.WORKFLOW_ID;
                    if (!workflowId) return;
                    workflowExists = true;
                    // If there is a restricted workflow type, find values
                    // Only continue if the vol has an assigned workflow that matches the value
                    const restrictTypeObj = this.config?.restrictWorkflowTypes;
                    if (restrictTypeObj) {
                        const restrictTypes = Object.entries(restrictTypeObj);
                        let filterTypes = restrictTypes
                            .filter((workflowOption) => workflowOption[1])
                            .map((workflowOption) => Number(workflowOption[0]));
                        if (filterTypes.length && filterTypes.indexOf(workflowId) === -1) return;
                    }
                    // Else assign workflow information
                    // Process workflows for "waiting" and/or for currently selected tab
                    let workflowStatus = assignment.AFF_ASSIGNMENT_WORKFLOW_STATUS[workflowId];
                    if (!workflowStatus) workflowStatus = 'In Progress';
                    if (!workflowId || ((workflowStatus === 'Completed' || workflowStatus === 'Not Started') && !this.controls.SHOW_HIDDEN_WORKFLOWS.value)) return;
                    let wfTabId = assignment.ID + '_' + workflowId;
                    // Exit if we have a tab configured already
                    if (this.workflowTabs.find((tab) => tab.wfTabId === wfTabId)) return;
                    // let loading = !this.workflowTabsSettings[wfTabId] || this.workflowTabsSettings[wfTabId].loading;
                    // Get step status from the itemList component
                    if (!this.workflowStepStatus[wfTabId]) this.workflowStepStatus[wfTabId] = {};
                    let stepStatuses = this.workflowStepStatus[wfTabId];
                    // Don't show workflows that are "Waiting" and don't have an enabled step
                    // TODO fix this to work elsewhere
                    let show = (workflowStatus === 'Waiting' && stepStatuses.enable) || true;
                    // Set global show statements
                    // (we have to show it on loading, because we will need to parse the steps in the itemList component code)
                    // if (loading) show = true;
                    // if (this.waitingFirstWorkflowLoad) show = false;
                    if (this.controls.SHOW_HIDDEN_WORKFLOWS.value) show = true;
                    let newTab = {
                        title: wfRecord.ORG_TITLE + ': ' + wfRecord.WORKFLOW_TITLE,
                        useVueSlot: true,
                        slotTemplateId: 'tabList_item_' + tabIndex,
                        assignmentId: assignment.ID,
                        orgId: aOrgId,
                        show: this.contextOrgId ? this.contextOrgId === aOrgId : true,
                        workflowId,
                        wfTabId,
                        workflowStatus,
                        tabIndex,
                        loading: true
                    };
                    // TODO if the tab doesn't already exist
                    this.workflowTabs.push(newTab);
                    if (this.selectedTab === null && newTab.show) this.selectedTab = tabIndex;
                    if (this.waitingFirstWorkflowLoad) this.waitingFirstWorkflowLoad = false;
                    tabIndex++;
                });
            });
            this.updateTabVisibility();
            if (this.workflowExists && !workflowExists) this.workflowExists = false;
            // TODO finish configuring the sort, doesn't work with stepStatuses as currently configured
            // tabs.sort((a, b) => a.workflowId - b.workflowId)
            //     .map((tab, index) => {
            //         tab.slotTemplateId = 'tabList_item_' + index;
            //         return tab;
            //     });
        },
        loadWorkflowDetails(tabDetails) {
            if (typeof tabDetails !== 'object') return;
            let { wfTabId, assignmentId, workflowId } = tabDetails;
            if (!this.workflowTabsSettings[wfTabId]) this.workflowTabsSettings[wfTabId] = {};
            if (!this.workflowTabsSettings[wfTabId].initialLoadComplete) {
                // library.showLoadingIndicator();
                // TODO refactor this to put workflows inside of recordStore or configStore
                let volId = this.contextVolId || "";
                let wfRequestFields = {
                    11: this.shiftFields,
                    32: this.affFields
                };
                this.sDs.post('sds_getWorkflowDetails', volId, assignmentId, workflowId, wfRequestFields, { strictTypeResults: true })
                    .then((response) => {
                        if (response.status === 'success' && response.data) {
                            let resData = response.data;
                            let formatWorkflowActivityEntities = (workflowDetails, activityEntitiesDetails) => {
                                workflowDetails.ACTIVITY_ENTITY = [];
                                for (let aeId in workflowDetails.ACTIVITY_ENTITY_ID) {
                                    if (activityEntitiesDetails[workflowDetails.ACTIVITY_ENTITY_ID[aeId]]) {
                                        workflowDetails.ACTIVITY_ENTITY.push(activityEntitiesDetails[workflowDetails.ACTIVITY_ENTITY_ID[aeId]]);
                                    }
                                }
                                return workflowDetails;
                            };
                            let formatAssignmentIds = (records, affiliations, entryPointIds) => {
                                let output = {
                                    LINKED_RECORDS: {},
                                    LINKED_AFFILIATIONS: {},
                                    ENTRY_POINT_RECORDS: {
                                        hierarchy1: { ID: entryPointIds[0] },
                                        hierarchy2: { ID: entryPointIds[1] },
                                    }
                                };
                                for (let key in records) {
                                    if (key === 'length') continue;
                                    if (key === 'ORGANIZATIONS') {
                                        output[key] = records[key];
                                        continue;
                                    }
                                    output.LINKED_RECORDS[key] = { ID: records[key] };
                                    output.LINKED_AFFILIATIONS[key] = { ID: affiliations[key] };
                                }
                                return output;
                            };
                            // get Lbes
                            for (let reswfId in resData.WORKFLOW) {
                                let workflowResponse = resData.WORKFLOW[reswfId];
                                // TODO get this to work for separate workflows
                                this.workflowTabsSettings[wfTabId] = resData.ACTIVITY_ENTITY && formatWorkflowActivityEntities(workflowResponse, resData.ACTIVITY_ENTITY);
                                this.workflowTabsSettings[wfTabId].loading = true;
                                // Update relatedRecords in data()
                                let relatedRecords = {
                                    activities: workflowResponse.ACTIVITY_ENTITY,
                                    affiliations: {},
                                    lbes: {},
                                    waivers: {},
                                    trainings: {},
                                    // shifts: {},  // accessed through the recordStore
                                    ASSIGNMENT_DETAIL_RECORD: {},
                                    ASSIGNMENT: {},
                                    assignment: {},
                                    VOL_AE_AFFILIATION_ID: workflowResponse.VOL_AE_AFFILIATION_ID
                                };
                                if (!this.serverAffiliations) this.serverAffiliations = {};
                                this.serverAffiliations[wfTabId] = resData.AFFILIATION;
                                if (resData.LOGBOOK_ENTRIES) relatedRecords.lbes = resData.LOGBOOK_ENTRIES;
                                // TODO refactor these to exclude relatedRecords and just use the resData with keys like WAIVERS
                                // ie: Don't convert them to lowercase, it's not necessary
                                if (resData.WAIVERS) relatedRecords.waivers = resData.WAIVERS;
                                if (resData.TRAININGS) relatedRecords.trainings = resData.TRAININGS;
                                if (resData.SCHEDULE_SHIFT_PLACEMENTS) {
                                    // Merge vol's shift placements with recordStore
                                    library.mergeObj(this.recordStore.records.vol[volId].shifts, resData.SCHEDULE_SHIFT_PLACEMENTS);
                                    this.recordStore.setModified({ vol: [volId] });
                                }
                                if (resData.ASSIGNMENT) {
                                    relatedRecords.ASSIGNMENT = resData.ASSIGNMENT[resData.ASSIGNMENT_ID];
                                    // This is the assignment with basic records instead of IDs
                                    relatedRecords.assignment = formatAssignmentIds(
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].LINKED_RECORDS,
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].LINKED_AFFILIATIONS,
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].ENTRY_POINT_ID
                                    );
                                }
                                let endpointRecord = resData.ASSIGNMENT_DETAIL_RECORD_ID
                                    && resData.ASSIGNMENT_DETAIL_RECORD[resData.ASSIGNMENT_DETAIL_RECORD_ID];
                                if (endpointRecord) {
                                    relatedRecords.ASSIGNMENT_DETAIL_RECORD = endpointRecord;
                                    relatedRecords.DETAIL_RECORD_ID = endpointRecord.DETAIL_RECORD_ID[0];
                                }
                                this.relatedRecords[wfTabId] = relatedRecords;
                            }
                            this.workflowTabsSettings[wfTabId].initialLoadComplete = true;
                        }
                        // if response is failure leave the workflowTabsSettings[wfTabId] as an empty object
                        library.hideLoadingIndicator();
                    });
            }
        }
    },
    components: {
        'item-list': defineAsyncComponent(() => import('@common/itemList.vue')),
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue'))
    }
};

componentStore.initComponent(workflowViewerCmp, workflowViewerConfig);

export default workflowViewerCmp;
</script>
<style>
/* Loader TODO: Move this to the loader component */
.loader-animation-container {
    display: inline-block;
    width: 60px;
}
.loader-animation {
    display: grid;
    grid-template-columns: repeat(5, 10px);
    gap: 3px;
    justify-content: center;
    align-items: center;
}
.ball {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    animation: wave 1.5s ease-in-out infinite;
    background-color:#EC135E;
}
.ball:nth-child(2) {
    animation-delay: -0.2s;
    background-color:#EF7D22;
}
.ball:nth-child(3) {
    animation-delay: -0.4s;
    background-color:#F7CE46;
}
.ball:nth-child(4) {
    animation-delay: -0.6s;
    background-color:#8BA93D;
}
.ball:nth-child(5) {
    animation-delay: -0.8s;
    background-color:#00ADC5;
}
@keyframes wave {
    0%, 100% {
        transform: translateY(6px);
    }
    50% {
        transform: translateY(-6px);
    }
}
/* End Loader */
</style>
<style scoped>
.no-workflows-message {
    margin-bottom: 0;
    padding: 15px;
    font-size: 16px;
}
:deep(.expiration-status) {
    font-size: 90%;
    margin-top: 15px;
}
:deep(.expiration-flag) {
    margin-right: 10px;
    font-size: 16px;
}
:deep(.current) { color: #16a34a }
:deep(.expiring) { color: #FB8432 }
:deep(.expired) { color: #dc2626 }
:deep(.activity-expiration) {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid #FB8432;
    border-radius: 8px;
    background: rgba(202, 138, 4, 0.1);
    width: fit-content
}
:deep(.activity-expiration > .expiration-text) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: #000
}
:deep(.activity-expiration > .expiration-text i) {
    font-size: 1.5rem;
    margin-right: 0.5rem;
    color: #FB8432
}
:deep(.activity-expiration > button) {
    background: #FB8432;
    border-radius: 12px;
    color: #fff;
    text-decoration: none
}
:deep(.activity-expiration > button:hover) {
    background: #fd9249;
}
:deep(.aff-info-text) {
    display: inline-block;
    padding-right: 8px;
}
:deep(.coordinator-actions), :deep(.coordinator-info) {
    display: inline-block;
    margin-top: 15px;
}
:deep(.coordinator-info) {
    border-right: 1px solid #bbbbbb;
}
:deep(.coordinator-action-button) {
    border: 0 none;
    transition: background .1s ease-in;
    background: rgba(255, 255, 255, .20);
    border-radius: 15px;
    color: #2b2b86;
    padding: 5px 15px;
    margin: 5px;
    font-size: 14px;
    display: inline-block;
    text-decoration: underline;
}
:deep(.coordinator-action-button:hover) {
    background: rgba(0, 0, 0, .08);
    transition: all .1s ease-in-out;
}
:deep(.history-button .e-icons) {
    margin-right: 3px;
    position: relative;
    top: 2px;
    font-size: 13px;
}
.workflow-header {
    display: flex;
    padding: 5px;
    border-bottom: 1px solid #b0b0b0;
    color: #383c50;
}
.workflow-loader p{
    display: inline-block;
    line-height: 32px;
    margin: 0 0 0 15px;
    vertical-align: top;
}
.workflow-status {
    text-align: right;
    flex-grow: 9;
    font-size: 16px;
    font-weight: bold;
}
.assignment-id {
    font-size: 14px;
}
:deep(.step-not-visible) {
    display: none;
}
</style>
